import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { PermissionsTreeComponent } from '../permissions-tree/permissions-tree.component';
import { IPermissionTreeOutput } from '../../models/permission-tree-output.model';
import { IAssignRevokePermissionsClient } from '../../clients/i-assign-revoke-permissions.client';
import { AssignRevokePermissionsClientProvider } from '../../clients/assign-revoke-permissions-client.provider';
import { ToastService } from '../../../../../../trash/shared/components/toasters/Toast.service';
import { createValidationError, handleApiErrors } from '../../../../../core/data-access/http-client/interceptors/error.interceptor';
import { ErrorOrError } from '../../../../../core/results/error-or/models/error-or-error.model';
import { DynamicDialogConfig, DynamicDialogRef, DynamicDialogModule } from 'primeng/dynamicdialog';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LoaderService } from '../../../../../../trash/shared/components/loader/loader.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-assign-revoke-permissions',
  standalone: true,
  imports: [CommonModule, PermissionsTreeComponent, DynamicDialogModule, TranslateModule],
  templateUrl: './assign-revoke-permissions.component.html',
  styleUrls: ['./assign-revoke-permissions.component.scss'],
})
export class AssignRevokePermissionsComponent {
  // Injected services
  private toastService = inject(ToastService);
  private loaderService = inject(LoaderService);
  private dialogReference = inject(DynamicDialogRef);
  private translate = inject(TranslateService);
  private client: IAssignRevokePermissionsClient = inject(AssignRevokePermissionsClientProvider).getClient();
  protected endPoint: string = inject(DynamicDialogConfig).data?.endPoint;

  // Permission output state
  permissionTreeOutput = signal<IPermissionTreeOutput | undefined>(undefined);

  // Handle permission tree changes
  handlePermissionChanged($event: IPermissionTreeOutput): void {
    this.permissionTreeOutput.set($event);
  }

  // Submit permissions
  handleSubmit(): void {
    const operations$ = this.getSubmitOperations();

    if (!operations$) {
      this.handleNoPermissionChangeError();
      return;
    }

    this.loaderService.show();

    operations$.subscribe({
      next: () => {
        this.showSuccessMessage();
        this.dialogReference.close();
      },
      error: (err) => {
        this.handleError(err);
      },
      complete: () => {
        this.loaderService.hide(); // Ensure loader is hidden after completion or error
      },
    });
  }

  // Generate permission submission observables
  private getSubmitOperations():
    | Observable<{
      assign: void | null;
      revoke: void | null;
    }>
    | undefined {
    const output = this.permissionTreeOutput();

    if (!output || (output.permissionIdsToAssign.length === 0 && output.permissionIdsToRevoke.length === 0)) {
      return undefined;
    }

    const { permissionIdsToAssign, permissionIdsToRevoke } = output;

    return forkJoin({
      assign: permissionIdsToAssign.length
        ? this.client.assign(this.endPoint, { ids: permissionIdsToAssign }).pipe(
          catchError((err) => {
            this.handleError([err]);
            return of(null); // Prevent forkJoin from failing entirely
          })
        )
        : of(null),
      revoke: permissionIdsToRevoke.length
        ? this.client.revoke(this.endPoint, { ids: permissionIdsToRevoke }).pipe(
          catchError((err) => {
            this.handleError([err]);
            return of(null); // Prevent forkJoin from failing entirely
          })
        )
        : of(null),
    });
  }

  // Handle the case where no permissions are changed
  private handleNoPermissionChangeError(): void {
    const validationError = createValidationError(
      this.translate.instant('GENERAL.ERROR_OCCURED'),
      this.translate.instant('FORMS.PLZ_SELECT_PERMISSIONS_BEFORE_SUBMIT')
    );
    handleApiErrors(validationError, this.toastService);
  }

  // Display success message
  private showSuccessMessage(): void {
    this.toastService.showSuccess(
      this.translate.instant('GENERAL.REQUEST_FULFILLED'),
      this.translate.instant('FORMS.PERMISSIONS_SUCCESSFULLY')
    );
  }

  // Handle errors from API responses
  private handleError(errors: ErrorOrError[]): void {
    handleApiErrors(errors, this.toastService);
    this.loaderService.hide(); // Ensure loader is hidden even if there's an error
  }

  // Close dialog
  onClose(data?: any): void {
    this.dialogReference.close(data);
  }
}
